import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { DialogActions, DialogContent, DialogTitle, Link } from "@material-ui/core";
import { Check } from "@material-ui/icons";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import * as StripeUtils from "common/utils/stripeUtils";
import stripeProducts from "common/stripeProducts";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import Analytics from "js/analytics";
import { getStaticUrl } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import { Button } from "js/Components/Button";
import {
    BeautifulDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog
} from "js/react/components/Dialogs/BaseDialog";
import { Gap10, Gap20 } from "js/react/components/Gap";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import CancelOrDowngradePlanDialog from "js/react/views/UserOptions/dialogs/CancelOrDowngradePlanDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { defaultText, largeTitle, smallText } from "js/textStyles";
import { themeColors } from "js/react/sharedStyles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import EndTrialDialog from "js/react/views/Billing/dialogs/EndTrialDialog";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import UpgradeToTeamWorkspaceDialog from "js/react/views/Billing/dialogs/UpgradeToTeamWorkspaceDialog";
import UpgradeToProWorkspaceDialog from "js/react/views/Billing/dialogs/UpgradeToProWorkspaceDialog";
import SalesFormDialog from "js/react/components/Dialogs/SalesFormDialog";
import { t } from "js/i18n";
const logger = getLogger(LogGroup.BILLING);

const StyledBeautifulDialog = styled(BeautifulDialog)`
    .MuiDialog-paperWidthSm {
        max-width: 700px;
        padding: 0;
    }
`;

enum PlanButtonAction {
    CANCEL_PLAN = "Cancel plan",
    CONTACT_SALES = "Contact Sales",
    CURRENT_PLAN_NOOP = "Current plan",
    DOWNGRADE_PLAN = "Downgrade",
    UPGRADE_PLAN = "Upgrade",
    UPGRADE_PLAN_NOOP = "Upgrade ", // The trailing space is intentional as a quick way to distinguish it from UPGRADE_PLAN in logic checks
    REACTIVATE_PLAN = "Reactivate plan",
}

const StyledDialogTitle = styled(DialogTitle)`
    padding-bottom: 0;
    
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        padding-top: 6px;
        
        p {
            ${defaultText}
            color: ${themeColors.mediumGray};
            margin: 0;
        }
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        display: flex;
        flex-direction: row;
        justify-content: center;
        padding: 0;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    && {
        padding-top:16px;
        padding-right: 38px;
        padding-bottom: 28px;
    }
`;

const PlanName = styled.div`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
`;

const PlanDescriptionGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    align-items: center;
`;

const PlanDescriptionFirstRow = styled.div`
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
`;

const PlanDescriptionSecondRow = styled.div`
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
`;

const PlanDescriptionOnlyRow = styled.div`
    display:flex;
    align-items: flex-end;
    color: #666;
    font-family: Source Sans Pro;
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    min-height: 28px;
    vertical-align: bottom;
    padding-bottom: 2px;
`;

const PriceCell = styled.div`
    grid-row: span 2;
    ${largeTitle}
    color: #11A9E2;
    margin-right: 8px;
`;

const PlanFeature = styled.div`
    ${smallText}
`;

const UpgradeIcon = styled(Check)`
    && {
        color: #1F1F1F;
        font-size: 16px;
        margin-right: 8px;
    }
`;

const ActionButton = styled(Button) <{ action: PlanButtonAction }>`
    ${({ action }) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return `
                    background-color: #FB0000;
                    color: white;
                `;
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return `
                    background-color: #F1F1F1;
                    color: #A9A9A9;
                `;
            case PlanButtonAction.UPGRADE_PLAN_NOOP:
                return `
                    background-color: #F1F1F1;
                    color: #A9A9A9;
                `;
            default:
                return `background-color: #11A9E2;`;
        }
    }}
    /* Remove hover and pointer interaction when disabled */
    &:disabled,
    &[disabled] {
        pointer-events: none;
        cursor: default;
    }

    &:disabled:hover,
    &[disabled]:hover {
        background-color: inherit;
        color: inherit;
        filter: none;
        box-shadow: none;
    }
`;

const StartSubscriptionLink = styled(Link)`
    cursor: pointer;
`;

export interface ChangePlanDialogProps {
    closeDialog?: () => void;
    featuredPlan?: WorkspacePlanTier;
    analytics?: {
        cta: string;
    };
}

type CombinedProps = ChangePlanDialogProps & WorkspaceControllerState & BillingControllerState;
export const ChangePlanDialogBase = (props: CombinedProps) => {
    const {
        stripeData,
        plan: { tier },
        closeDialog,
        featuredPlan,
        analytics
    } = props;

    if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
        return <GuestWorkspaceDialog open={true} closeDialog={closeDialog} />;
    }

    const { subscription } = stripeData;
    const subscriptionCanceled = StripeUtils.Subscription.hasPendingCancellation(subscription);
    const subscriptionPastDue = StripeUtils.Subscription.hasPastDuePayment(subscription);
    const isInTrial = BillingController.isInTrial;
    const daysRemainingInTrial = isInTrial ? Math.ceil(
        (subscription?.trial_end * 1000 - new Date().getTime()) / 1000 / 60 / 60 / 24
    ) : null;

    const proPrice = stripeProducts[WorkspacePlanTier.PRO].plans.find(plan => plan.type === "recurring" && plan.interval === "year" && plan.isDefault)?.amount / 100;
    const teamPrice = stripeProducts[WorkspacePlanTier.TEAM].plans.find(plan => plan.type === "recurring" && plan.interval === "year" && plan.isDefault)?.amount / 100;

    const proHighlighted = (featuredPlan === WorkspacePlanTier.PRO) ||
        (!featuredPlan && [WorkspacePlanTier.BASIC, WorkspacePlanTier.PRO].includes(tier));

    const planLabels: Record<WorkspacePlanTier, string> = {
        [WorkspacePlanTier.BASIC]: t("Basic"),
        [WorkspacePlanTier.PRO]: t("Pro"),
        [WorkspacePlanTier.TEAM]: t("Team"),
        [WorkspacePlanTier.ENTERPRISE]: t("Enterprise")
    };

    useEffect(() => {
        Analytics.ModifyWorkspacePlan.ManagePlanDialogOpen({ featured_plan: featuredPlan, analytics });
    }, []);

    const handleCancelPlan = async () => {
        Analytics.ModifyWorkspacePlan.CancelFlowStart();
        closeDialog();
        ShowDialog(CancelOrDowngradePlanDialog, { action: "cancel" });
    };

    const handleReactivatePlan = async () => {
        Analytics.ModifyWorkspacePlan.ReactivateFlowStart();
        try {
            if (!subscription?.default_payment_method || subscription?.status === "past_due") {
                const shouldReactivate = await ShowDialogAsync(ChangePaymentMethodDialog);
                if (!shouldReactivate) {
                    return;
                }
            }

            await withProgressDialog({
                title: t("Reactivating {{plan}} plan...", { plan: planLabels[tier] }),
                action: () => BillingController.reactivateSubscription(),
            });

            Analytics.ModifyWorkspacePlan.ReactivateFlowComplete();
        } catch (err) {
            logger.error(err, "[CurrentPlanBlock] handleReactivatePlan() failed");

            ShowErrorDialog({
                error: t("An error occurred while reactivating your {{plan}} plan", { plan: planLabels[tier] }),
                message: (
                    <>
                        <p><strong>{t("Error")}:</strong> {err.message}</p>
                        <p>{t("We apologize for the inconvenience. Please contact us at {{email}}.", { email: "support@beautiful.ai" })}</p>
                    </>
                ),
            });
        }
    };
    const handleDowngradePlan = async () => {
        Analytics.ModifyWorkspacePlan.DowngradeFlowStart();
        closeDialog();
        ShowDialog(CancelOrDowngradePlanDialog, { action: "downgrade" });
    };

    const handleUpgradePlan = async (targetTier: WorkspacePlanTier) => {
        Analytics.ModifyWorkspacePlan.UpgradeFlowStart();

        closeDialog();

        if (targetTier === WorkspacePlanTier.PRO) {
            await ShowDialogAsync(UpgradeToProWorkspaceDialog, {
                onClose: result => {
                    if (!result) {
                        ShowDialog(ChangePlanDialogBase, props);
                    }
                }
            });
        } else if (targetTier === WorkspacePlanTier.TEAM) {
            ShowDialogAsync(UpgradeToTeamWorkspaceDialog, {
                onClose: result => {
                    if (!result) {
                        ShowDialog(ChangePlanDialogBase, props);
                    }
                }
            });
        }
    };

    const handleContactSales = () => {
        Analytics.ModifyWorkspacePlan.EnterpriseInquiry();
        closeDialog();
        ShowDialog(SalesFormDialog);
    };

    const handleProButtonAction = (action: PlanButtonAction) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return handleCancelPlan();
            case PlanButtonAction.CONTACT_SALES:
                return handleContactSales();
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return;
            case PlanButtonAction.DOWNGRADE_PLAN:
                return handleDowngradePlan();
            case PlanButtonAction.UPGRADE_PLAN:
                return handleUpgradePlan(WorkspacePlanTier.PRO);
            case PlanButtonAction.REACTIVATE_PLAN:
                return handleReactivatePlan();
        }
    };

    const handleTeamButtonAction = (action: PlanButtonAction) => {
        switch (action) {
            case PlanButtonAction.CANCEL_PLAN:
                return handleCancelPlan();
            case PlanButtonAction.CONTACT_SALES:
                return handleContactSales();
            case PlanButtonAction.CURRENT_PLAN_NOOP:
                return;
            case PlanButtonAction.DOWNGRADE_PLAN:
                return handleDowngradePlan();
            case PlanButtonAction.UPGRADE_PLAN:
                return handleUpgradePlan(WorkspacePlanTier.TEAM);
            case PlanButtonAction.UPGRADE_PLAN_NOOP:
                return;
            case PlanButtonAction.REACTIVATE_PLAN:
                return handleReactivatePlan();
        }
    };

    const getTitleText = () => {
        if (!featuredPlan) {
            return t("Choose the plan that's right for you.");
        } else if (featuredPlan === WorkspacePlanTier.TEAM) {
            return t("Unlock this feature when you upgrade to the Team plan.");
        } else {
            return t("Unlock this feature when you upgrade to the Pro plan.");
        }
    };

    const getProButtonAction = (): PlanButtonAction => {
        switch (tier) {
            case WorkspacePlanTier.BASIC:
                return PlanButtonAction.UPGRADE_PLAN;
            case WorkspacePlanTier.PRO:
                if (featuredPlan === WorkspacePlanTier.TEAM && !subscriptionCanceled) {
                    return PlanButtonAction.CURRENT_PLAN_NOOP;
                } if (featuredPlan === WorkspacePlanTier.TEAM && subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else if (subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else {
                    return PlanButtonAction.CANCEL_PLAN;
                }
            case WorkspacePlanTier.TEAM:
                return PlanButtonAction.DOWNGRADE_PLAN;
            case WorkspacePlanTier.ENTERPRISE:
                return PlanButtonAction.CONTACT_SALES;
        }
    };

    const getTeamButtonAction = (): PlanButtonAction => {
        switch (tier) {
            case WorkspacePlanTier.BASIC:
                return PlanButtonAction.UPGRADE_PLAN;
            case WorkspacePlanTier.PRO:
                if (subscriptionCanceled || subscriptionPastDue) {
                    return PlanButtonAction.UPGRADE_PLAN_NOOP;
                } else {
                    return PlanButtonAction.UPGRADE_PLAN;
                }
            case WorkspacePlanTier.TEAM:
                if (subscriptionCanceled) {
                    return PlanButtonAction.REACTIVATE_PLAN;
                } else {
                    return PlanButtonAction.CANCEL_PLAN;
                }
            case WorkspacePlanTier.ENTERPRISE:
                return PlanButtonAction.CONTACT_SALES;
        }
    };

    const handleStartSubscription = async () => {
        closeDialog();
        const subscriptionStarted = await ShowDialogAsync(EndTrialDialog, {});
        if (!subscriptionStarted) {
            ShowDialog(ChangePlanDialogBase, props);
        }
    };

    const FEATURES_BY_PLAN: Record<Exclude<WorkspacePlanTier, WorkspacePlanTier.BASIC>, string[]> = {
        [WorkspacePlanTier.PRO]: [
            t("Generate slides and presentations with AI"),
            t("Hundreds of Smart Slide templates"),
            t("Auto-formatting layouts"),
            t("Stunning data visualization"),
            t("Customize themes and styles"),
            t("Image and icon libraries"),
        ],
        [WorkspacePlanTier.TEAM]: [
            t("Everything in Pro"),
            t("Collaborative workspace"),
            t("Viewer analytics and tracking"),
            t("Centralized slide and template library"),
            t("Shared asset library"),
        ],
        [WorkspacePlanTier.ENTERPRISE]: [
            t("Everything in Team"),
            t("Unlimited Team Resources"),
            t("Advanced permissions"),
            t("User Provisioning (SCIM)"),
            t("Audit Events"),
            t("Dedicated Onboarding"),
            t("SSO (Single Sign-on)"),
            t("Priority support"),
        ],
    };

    const proButtonAction = useMemo(() => getProButtonAction(), [tier, subscriptionCanceled, isInTrial, featuredPlan]);
    const teamButtonAction = useMemo(() => getTeamButtonAction(), [tier, subscriptionCanceled, isInTrial]);

    return (
        <StyledBeautifulDialog
            closeDialog={closeDialog}
        >
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-upgrade-logo.svg")} />
                <Gap10 />
                <div>
                    {getTitleText()}
                </div>
                {isInTrial && !subscriptionCanceled &&
                    <p>
                        {t("You have {{count}} day left in your trial.", { count: daysRemainingInTrial })}
                        {" "}
                        <StartSubscriptionLink
                            onClick={handleStartSubscription}
                            data-test-id="change-plan-start-subscription-link"
                        >{t("Start your subscription")}</StartSubscriptionLink>
                    </p>
                }
            </StyledDialogTitle>
            <StyledDialogContent>
                <FlexBox column left top width="200px" padding="20px" style={{
                    backgroundColor: proHighlighted ? "#F7FAFC" : "",
                    border: proHighlighted ? "1px solid #DAE9F0" : ""
                }}>
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>{t("Pro")}{(subscriptionCanceled && tier === WorkspacePlanTier.PRO) &&
                            <span style={{ color: themeColors.warning }}> - {t("Canceled Plan")}</span>}</PlanName>
                        <PlanDescriptionGrid>
                            <PriceCell>${(proPrice / 12).toFixed(0)}</PriceCell>
                            <FlexBox column left>
                                <PlanDescriptionFirstRow>{t("per user/mo")}</PlanDescriptionFirstRow>
                                <PlanDescriptionSecondRow>{t("billed annually")}</PlanDescriptionSecondRow>
                            </FlexBox>
                        </PlanDescriptionGrid>
                        <ActionButton
                            blue
                            data-test-id="change-plan-pro-action-button"
                            action={proButtonAction}
                            disabled={tier === WorkspacePlanTier.PRO && featuredPlan === WorkspacePlanTier.TEAM && !subscriptionCanceled}
                            onClick={() => handleProButtonAction(proButtonAction)}

                        >
                            {t(proButtonAction)}
                        </ActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.PRO].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <UpgradeIcon />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
                <FlexBox column left top width="200px" padding="20px" style={{
                    backgroundColor: !proHighlighted ? "#F7FAFC" : "",
                    border: !proHighlighted ? "1px solid #DAE9F0" : ""
                }}>
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>{t("Team")}</PlanName>
                        <PlanDescriptionGrid>
                            <PriceCell>${(teamPrice / 12).toFixed(0)}</PriceCell>
                            <FlexBox column left>
                                <PlanDescriptionFirstRow>{t("per user/mo")}</PlanDescriptionFirstRow>
                                <PlanDescriptionSecondRow>{t("billed annually")}</PlanDescriptionSecondRow>
                            </FlexBox>
                        </PlanDescriptionGrid>
                        <ActionButton
                            blue
                            data-test-id="change-plan-team-action-button"
                            action={teamButtonAction}
                            onClick={() => handleTeamButtonAction(teamButtonAction)}
                        >
                            {t(teamButtonAction)}
                        </ActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.TEAM].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <UpgradeIcon />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
                <FlexBox column left top width="200px" padding="20px">
                    <FlexBox column gap={8} left fillWidth
                        style={{ borderBottom: "1px solid #EEE" }}>
                        <PlanName>{t("Enterprise")}</PlanName>
                        <FlexBox column left>
                            <PlanDescriptionOnlyRow>{t("Custom pricing available")}</PlanDescriptionOnlyRow>
                        </FlexBox>
                        <ActionButton
                            blue
                            onClick={handleContactSales}
                            data-test-id="change-plan-enterprise-action-button"
                        >
                            {t(PlanButtonAction.CONTACT_SALES)}
                        </ActionButton>
                        <Gap20 />
                    </FlexBox>
                    <FlexBox column left fillWidth>
                        {FEATURES_BY_PLAN[WorkspacePlanTier.ENTERPRISE].map(text => (
                            <FlexBox padding="12px 0px" fillWidth left style={{ borderBottom: "1px solid #EEEEEE" }}>
                                <UpgradeIcon />
                                <PlanFeature>{text}</PlanFeature>
                            </FlexBox>
                        ))}
                    </FlexBox>
                </FlexBox>
            </StyledDialogContent>
            <StyledDialogActions>
                <Button
                    unframed
                    large
                    color={themeColors.mediumGray}
                    data-test-id="change-plan-close-dialog"
                    onClick={closeDialog}
                    style={{ background: themeColors.lightGray }}
                >
                    {t("Not Now")}
                </Button>
            </StyledDialogActions>
        </StyledBeautifulDialog>
    );
};

export default WorkspaceController.withInitializedState(
    BillingController.withInitializedState(
        ChangePlanDialogBase
    )
);
